import { t } from '@hefe/i18n';
import {
  RequestBasketballPostGame,
  ResponseBasketballGetGameFormData,
} from '@hefe/models';
import {
  BasketballGameActions,
  BasketballGamesSlice,
  BasketballTeamsSlice,
  GymsSlice,
  ModalsSlice,
  currentUserIsAdminSelector,
  userState,
} from '@hefe/store';
import { Spin, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import CustomForm, {
  FormField,
} from '../../../core/components/form/custom-form';

interface AddEditGameFormProps {
  gameId?: number;
}

export default function AddEditGameForm(props: AddEditGameFormProps) {
  const { gameId } = props;

  const dispatch = useAppDispatch();

  const [requestData, setRequestData] =
    useState<ResponseBasketballGetGameFormData>();
  const [ownHomeTeam, setOwnHomeTeam] = useState(
    requestData?.home_team_id ? true : false
  );
  const [ownGuestTeam, setOwnGuestTeam] = useState(
    requestData?.guest_team_id ? true : false
  );
  const [isHomeGame, setHomeGame] = useState(requestData?.home_game ?? false);

  const [loadingRequestData, setLoadingRequestData] = useState(false);

  const stateUser = useSelector(userState);
  const userIsAdmin = useSelector(currentUserIsAdminSelector);

  const gyms = useSelector(GymsSlice.gymsSelectSelector);
  const teams = useSelector(
    BasketballTeamsSlice.basketballTeamsSelectWithoutPendingSelector
  );

  const formKey = `add-edit-game-form-${gameId ?? 'new'}`;

  useEffect(() => {
    if (gameId) {
      setLoadingRequestData(true);
      dispatch(BasketballGamesSlice.fetchBasketballGameFormData(gameId))
        .unwrap()
        .then((data) => {
          setRequestData(data);
          setOwnHomeTeam(data.home_team_id ? true : false);
          setOwnGuestTeam(data.guest_team_id ? true : false);
          setHomeGame(data.home_game ?? false);
        })
        .finally(() => setLoadingRequestData(false));
    }
  }, [gameId]);

  const onSubmit = (values: any, resetForm: any) => {
    const setLoading = ModalsSlice.modalsActions.setModalLoading;
    const close = ModalsSlice.modalsActions.closeModal;
    let gym = values.gym;

    if (isHomeGame) {
      const gymSelect = gyms.find((g) => g.value === values.gym);
      if (gymSelect) {
        gym = gymSelect.label;
      }
    }

    const submitData: RequestBasketballPostGame = {
      ...(requestData ? { id: requestData.id } : {}),
      ...values,
      date: dayjs(values.date).format('YYYY-MM-DD HH:mm:ss'),
      gym: gym,
      own_home_team: values.own_home_team || false,
      own_guest_team: values.own_guest_team || false,
      is_home: values.is_home || false,
      canceled: values.canceled || false,
      own_referees: values.own_referees || false,
      return_full_game: true,
    };

    dispatch(setLoading(true));
    dispatch(BasketballGameActions.saveBasketballGame(submitData))
      .unwrap()
      .then(() => dispatch(close()))
      .catch(() => null)
      .finally(() => dispatch(setLoading(false)));
  };

  const gymField = (): FormField => {
    if (!isHomeGame) {
      return {
        name: 'gym',
        label: t('gyms.singular'),
        type: 'text',
        initalValue: requestData?.gym,
        rules: [
          {
            required: true,
            message: t('form_errors.name'),
          },
        ],
      };
    } else {
      return {
        name: 'gym',
        label: t('gyms.singular'),
        type: 'select',
        initalValue: gyms.find((g) => g.label === requestData?.gym)?.value,
        selectOptions: gyms,
        rules: [
          {
            required: true,
            message: t('form_errors.select_value'),
          },
        ],
      };
    }
  };

  const teamInput = (home: boolean): FormField => {
    const inputName = `${home ? 'home' : 'guest'}_team_id`;
    const oppositeInputName = `${home ? 'guest' : 'home'}_team_id`;
    const label = home ? t('home') : t('guest');
    const ownTeam = home ? ownHomeTeam : ownGuestTeam;

    if (ownTeam) {
      return {
        name: inputName,
        label: label,
        type: 'select',
        selectOptions: teams,
        initalValue: home
          ? requestData?.home_team_id
          : requestData?.guest_team_id,
        rules: [
          {
            required: true,
            message: t('form_errors.select_value'),
          },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value) {
                return Promise.resolve();
              }
              if (value === getFieldValue(oppositeInputName)) {
                return Promise.reject(new Error(t('form_errors.same_team')));
              }
              if (!userIsAdmin) {
                const coachTeams = (
                  stateUser.user?.basketball_user?.coached_teams || []
                ).map((t) => t.id);
                const userOwnTeam = coachTeams.includes(value);
                const userOppositeTeam = coachTeams.includes(
                  getFieldValue(oppositeInputName)
                );
                if (!userOwnTeam && !userOppositeTeam) {
                  return Promise.reject(
                    new Error(t('form_errors.no_own_team'))
                  );
                }
              }

              return Promise.resolve();
            },
          }),
        ],
      };
    } else {
      return {
        name: home ? 'home' : 'guest',
        label: label,
        type: 'text',
        initalValue: home ? requestData?.home : requestData?.guest,
        rules: [
          {
            required: true,
            message: t('form_errors.name'),
          },
        ],
      };
    }
  };

  if (loadingRequestData) {
    return <Spin />;
  }

  return (
    <>
      {userIsAdmin && (
        <Typography.Paragraph>
          {t('games.form.external_info')}
        </Typography.Paragraph>
      )}
      <CustomForm
        key={formKey}
        formId={formKey}
        fields={[
          {
            name: 'date',
            label: t('date'),
            type: 'datetime',
            initalValue: requestData?.date,
            rules: [
              {
                required: true,
                message: t('form_errors.date'),
              },
            ],
          },
          {
            name: 'own_home_team',
            label: t('own_home_team'),
            type: 'checkbox',
            initalValue: ownHomeTeam,
            value: ownHomeTeam,
            onChange: (value) => setOwnHomeTeam(value),
            rules: [],
          },
          { ...teamInput(true) },
          {
            name: 'own_guest_team',
            label: t('own_guest_team'),
            type: 'checkbox',
            initalValue: ownGuestTeam,
            value: ownGuestTeam,
            onChange: (value) => setOwnGuestTeam(value),
            rules: [],
          },
          { ...teamInput(false) },
          {
            name: 'is_home',
            label: t('home_game'),
            type: 'checkbox',
            initalValue: isHomeGame,
            value: isHomeGame,
            onChange: (value) => setHomeGame(value),
            rules: [],
          },
          { ...gymField() },
          {
            name: 'own_referees',
            label: t('own_referees'),
            type: 'checkbox',
            initalValue: requestData?.own_referees,
            rules: [],
          },
          {
            name: 'canceled',
            label: t('canceled'),
            type: 'checkbox',
            initalValue: requestData?.canceled,
            rules: [],
          },
        ]}
        onSubmit={onSubmit}
      />
    </>
  );
}
